import { useState } from 'react';
import { downloadCsvTemplate } from '../utils/downloadTemplate';

export default function UploadModal({ title, templateHint, fields, onUpload, onClose }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function handleUpload() {
    if (!file) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await onUpload(file);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        {templateHint && <p className="text-xs text-gray-500 mb-3">{templateHint}</p>}

        {fields?.length > 0 && (
          <button
            type="button"
            onClick={() =>
              downloadCsvTemplate(
                `${title.replace(/\s+/g, '-').toLowerCase()}-template.csv`,
                fields.map((f) => f.name)
              )
            }
            className="mb-3 text-xs text-blue-600 hover:underline"
          >
            Download blank template (CSV)
          </button>
        )}

        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="block w-full text-sm text-gray-700 border border-gray-300 rounded px-3 py-2 mb-4"
        />

        {error && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}

        {result && (
          <div className="mb-4 text-sm bg-gray-50 border border-gray-200 rounded p-3 max-h-56 overflow-auto">
            <div className="font-medium text-gray-800 mb-1">
              Total: {result.total} · Inserted: {result.inserted ?? 0}
              {result.updated !== undefined && ` · Updated: ${result.updated}`} · Failed: {result.failed}
            </div>
            {result.transactionsSynced > 0 && (
              <div className="text-amber-700 mb-1">
                Synced Channel/Sub-Channel onto {result.transactionsSynced} existing sales transactions
              </div>
            )}
            {result.errors?.length > 0 && (
              <ul className="list-disc list-inside text-red-600 space-y-0.5">
                {result.errors.map((e, i) => (
                  <li key={i}>
                    Row {e.row}: {e.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
          >
            {loading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}
